import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
	Box,
	Button,
	Paper,
	Typography,
	CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import Pagination from "@mui/material/Pagination";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import type { TooltipProps } from "recharts";
import type { Payload } from "recharts/types/component/DefaultTooltipContent";

import {
	fetchData,
	FetchDataFailureAction,
	FetchDataSuccessAction,
} from "../../../store/actions";
import { useCustomToast } from "../../Methods/SnackBar";
import { getProjectVelocityData, jiraChartResponseProfile } from "../../../Service/Apis";
import MonthButtonGroup from "../../utilization/MonthsButtonGroup";
import { jiraChartBlobDownload } from "../chartExport";
import { RootState } from "../../../store/Store";
import ProjectSprintFilter from "../ProjectFilter/ProjectSprintFilter";

// Constants

const ITEMS_PER_PAGE = 3;
const CHART_HEIGHT = 400;
const DELIMITER = "::";

// Velocity key used in the flattened chart-data objects
const VELOCITY_KEY = "avg_velocity";

// Types

interface VelocitySprint {
	sprint_name: string;
	start_date: string | null;
	end_date: string | null;
	sprint_state: string;
	completed_story_points: number;
	completed_issues_count: number;
	average_velocity: number | null;
}

interface VelocityProject {
	project_name: string;
	project_key: string;
	sprints: VelocitySprint[];
}

// Helpers

/** Deterministic colour from a string (shared with other project charts). */
const PROJECT_COLORS: Record<string, string> = {};
function hashStringToColor(str: string): string {
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	return `hsl(${Math.abs(hash) % 360}, 65%, 50%)`;
}
function getProjectColor(key: string): string {
	if (!PROJECT_COLORS[key]) PROJECT_COLORS[key] = hashStringToColor(key);
	return PROJECT_COLORS[key];
}

/** Returns an array of project_key strings — used as stable selection identifiers. */
function getAvailableProjectKeys(data: VelocityProject[]): string[] {
	return data.map((p) => p.project_key);
}

/** Builds a project_key → project_name lookup for display labels. */
function buildProjectNameMap(data: VelocityProject[]): Record<string, string> {
	return Object.fromEntries(data.map((p) => [p.project_key, p.project_name]));
}

function getAvailableSprints(data: VelocityProject[]): string[] {
	const sprints = new Set<string>();
	data.forEach((p) => p.sprints.forEach((s) => sprints.add(s.sprint_name)));
	return Array.from(sprints);
}

/**
 * Each entry represents one sprint (X axis); each project contributes a
 * `<projectName>::avg_velocity` key (Y axis bar).
 */
function transformDataForChart(data: VelocityProject[]) {
	const allSprints = new Set<string>();
	data.forEach((p) => p.sprints.forEach((s) => allSprints.add(s.sprint_name)));

	return Array.from(allSprints).map((sprintName) => {
		const point: Record<string, string | number | null> = { sprint_name: sprintName };
		data.forEach((project) => {
			const sprint = project.sprints.find((s) => s.sprint_name === sprintName);
			if (sprint) {
				// Use project_key as the stable data key prefix (not project_name)
				point[`${project.project_key}${DELIMITER}${VELOCITY_KEY}`] =
					sprint.average_velocity !== null ? sprint.average_velocity : 0;
				// Stash raw counts so the tooltip can read them
				point[`${project.project_key}${DELIMITER}completed_sp`] =
					sprint.completed_story_points;
				point[`${project.project_key}${DELIMITER}done_issues`] =
					sprint.completed_issues_count;
			}
		});
		return point;
	});
}

// Custom Tooltip

const VelocityTooltip: React.FC<TooltipProps<number, string>> = ({
	active,
	payload,
	label,
}) => {
	if (!active || !payload?.length) return null;

	// Each bar entry corresponds to one project's velocity for this sprint.
	// We only render the velocity bars (skip the hidden completed_sp / done_issues keys).
	const velocityEntries = payload.filter((e: Payload<number, string>) =>
		String(e.dataKey).endsWith(`${DELIMITER}${VELOCITY_KEY}`)
	);

	if (!velocityEntries.length) return null;

	return (
		<Box
			sx={{
				bgcolor: "#fff",
				p: "12px 16px",
				border: "1px solid #e0e0e0",
				borderRadius: "8px",
				boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
				maxHeight: 320,
				overflowY: "auto",
				maxWidth: 420,
			}}
		>
			<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
				{label}
			</Typography>
			{velocityEntries.map((entry: Payload<number, string>) => {
				const delimIdx = String(entry.dataKey).indexOf(DELIMITER);
				// dataKey is "<projectKey>::avg_velocity" — extract the key prefix
				const projectKey = delimIdx !== -1
					? String(entry.dataKey).slice(0, delimIdx)
					: String(entry.dataKey);

				// Pull the hidden counts from the same chart-data row
				const row = entry.payload as Record<string, any>;
				const completedSP: number = row[`${projectKey}${DELIMITER}completed_sp`] ?? 0;
				const doneIssues: number = row[`${projectKey}${DELIMITER}done_issues`] ?? 0;
				const velocity: number = typeof entry.value === "number" ? entry.value : 0;
				// Display name comes from the Bar's name prop (set to project_name in chartBars)
				const projectDisplayName = String(entry.name ?? projectKey)
					.replace(/\s*[—-]\s*Avg Velocity$/, "");

				return (
					<Box
						key={entry.dataKey}
						sx={{ py: "4px", borderTop: "1px solid #f0f0f0", "&:first-of-type": { borderTop: "none" } }}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "2px" }}>
							<Box
								sx={{
									width: 10,
									height: 10,
									borderRadius: "50%",
									bgcolor: entry.color,
									flexShrink: 0,
								}}
							/>
							<Typography variant="body2" sx={{ fontWeight: 600, fontSize: "12px" }}>
								{projectDisplayName}
							</Typography>
						</Box>
						<Box sx={{ pl: "16px" }}>
							<Typography variant="body2" sx={{ color: "#555", fontSize: "12px" }}>
								Avg Velocity: <strong>{velocity.toFixed(2)} SP / task</strong>
							</Typography>
							<Typography variant="body2" sx={{ color: "#555", fontSize: "12px" }}>
								Completed Story Points: <strong>{completedSP}</strong>
							</Typography>
							<Typography variant="body2" sx={{ color: "#555", fontSize: "12px" }}>
								Resolved Tasks: <strong>{doneIssues}</strong>
							</Typography>
						</Box>
					</Box>
				);
			})}
		</Box>
	);
};

// Main Component

const ProjectVelocityChart: React.FC = () => {
	const dispatch2: ThunkDispatch<
		RootState,
		null,
		FetchDataSuccessAction | FetchDataFailureAction
	> = useDispatch();

	const [projectData, setProjectData] = useState<VelocityProject[]>([]);
	const [selectedFormat] = useState<string>("excel");
	const [month, setMonth] = useState<string>("1");
	const [loading, setLoading] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
	const [selectedSprints, setSelectedSprints] = useState<string[]>([]);
	const [page, setPage] = useState(1);
	const showToast = useCustomToast();

	const nrole = useSelector((state: RootState) => state.auth.selectedRole);

	const paramsData = useMemo<Record<string, string>>(
		() => ({ ...jiraChartResponseProfile, role: nrole ?? "Employee" }),
		[nrole]
	);

	// Data fetching

	const fetchVelocityData = useCallback(
		async (selectedMonth: string) => {
			try {
				setLoading(true);
				const res = await dispatch2(
					fetchData(`${getProjectVelocityData}${selectedMonth}`, paramsData)
				);
				setMonth(selectedMonth);
				setProjectData(Array.isArray(res) ? (res as VelocityProject[]) : []);
			} catch (error) {
				console.error("ProjectVelocityChart fetch error:", error);
			} finally {
				setLoading(false);
			}
		},
		[dispatch2, paramsData]
	);

	useEffect(() => {
		fetchVelocityData("1");
	}, [fetchVelocityData]);

	// selectedProjects stores project_key values (stable identifiers, not display names).
	useEffect(() => {
		if (projectData.length > 0) {
			setSelectedProjects(getAvailableProjectKeys(projectData));
			setSelectedSprints(getAvailableSprints(projectData));
		}
	}, [projectData]);

	// Reset to page 1 whenever filters change
	useEffect(() => {
		setPage(1);
	}, [selectedProjects, selectedSprints]);

	// Handlers

	const buttonData = [
		{ label: "1M", onClick: () => fetchVelocityData("1") },
		{ label: "3M", onClick: () => fetchVelocityData("3") },
		{ label: "6M", onClick: () => fetchVelocityData("6") },
		{ label: "1Y", onClick: () => fetchVelocityData("12") },
	];

	const onDownload = async () => {
		try {
			setDownloading(true);
			const ext = selectedFormat === "excel" ? "xlsx" : selectedFormat;
			await jiraChartBlobDownload(
				`${getProjectVelocityData}${month}`,
				{ ...paramsData, download: selectedFormat } as Record<string, unknown>,
				`project_velocity.${ext}`,
				showToast
			);
		} finally {
			setDownloading(false);
		}
	};

	const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
	};

	// Derived data

	const filteredData = useMemo<VelocityProject[]>(
		() =>
			projectData
				.filter((p) => selectedProjects.includes(p.project_key))
				.map((p) => ({
					...p,
					sprints: p.sprints.filter((s) => selectedSprints.includes(s.sprint_name)),
				})),
		[projectData, selectedProjects, selectedSprints]
	);

	const allChartData = useMemo(() => transformDataForChart(filteredData), [filteredData]);

	const paginatedData = useMemo(() => {
		const start = (page - 1) * ITEMS_PER_PAGE;
		return allChartData.slice(start, start + ITEMS_PER_PAGE);
	}, [allChartData, page]);

	const projectNameMap = useMemo(() => buildProjectNameMap(projectData), [projectData]);

	const chartBars = useMemo(
		() =>
			selectedProjects.map((projectKey) => {
				const displayName = projectNameMap[projectKey] ?? projectKey;
				return (
					<Bar
						key={projectKey}
						dataKey={`${projectKey}${DELIMITER}${VELOCITY_KEY}`}
						name={`${displayName} — Avg Velocity`}
						fill={getProjectColor(projectKey)}
						barSize={60}
						maxBarSize={80}
					/>
				);
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedProjects, paginatedData.length, projectNameMap]
	);

	// Render

	return (
		<Paper sx={{ m: 2, p: 4 }}>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					flexDirection="column"
					gap={2}
				>
					<Typography variant="h4">Project Velocity Chart</Typography>
					<MonthButtonGroup buttonData={buttonData} />
				</Box>

				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					flexDirection="column"
					gap={2}
				>
					<Box mb={3}>
						<ProjectSprintFilter
							projects={getAvailableProjectKeys(projectData)}
							selectedProjects={selectedProjects}
							sprints={getAvailableSprints(projectData)}
							selectedSprints={selectedSprints}
							onProjectChange={setSelectedProjects}
							onSprintChange={setSelectedSprints}
							projectData={projectData}
							projectNameMap={projectNameMap}
						/>
					</Box>

					{downloading ? (
						<Box display="flex" justifyContent="center" alignItems="center" gap={2}>
							<CircularProgress size="1rem" />
							<Typography>Downloading...</Typography>
						</Box>
					) : (
						<Button variant="outlined" size="small" onClick={onDownload}>
							Export
						</Button>
					)}
				</Box>
			</Box>

			{loading ? (
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					height={CHART_HEIGHT}
				>
					<CircularProgress />
				</Box>
			) : (
				<>
					<ResponsiveContainer width="100%" height={CHART_HEIGHT}>
						<BarChart data={paginatedData} barGap={5} barCategoryGap={20}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="sprint_name" />
							<YAxis
								label={{
									value: "Avg Velocity (SP / task)",
									angle: -90,
									position: "insideLeft",
									offset: 10,
									style: { fontSize: 11 },
								}}
							/>
							<Tooltip
								content={<VelocityTooltip />}
								wrapperStyle={{ zIndex: 9999, pointerEvents: "auto" }}
							/>
							{chartBars}
						</BarChart>
					</ResponsiveContainer>

					<Box display="flex" justifyContent="center" mt={2}>
						<Pagination
							count={Math.ceil(allChartData.length / ITEMS_PER_PAGE)}
							page={page}
							onChange={handlePageChange}
							color="primary"
						/>
					</Box>
				</>
			)}
		</Paper>
	);
};

export default ProjectVelocityChart;
